/**
 * Typed endpoint functions — one per API operation the UI needs. Components call
 * these instead of touching fetch directly, so URLs and shapes live in one file.
 */

import { http, buildSearchQuery } from "./client";
import type {
  BlogPost,
  BlogPostSummary,
  Collection,
  CommentDto,
  CommentRequest,
  CommentThreadResponse,
  CommentVoteDto,
  ContactRequest,
  ContentType,
  DocumentDto,
  ExternalResource,
  PageResponse,
  Photo,
  SearchParams,
  SearchResponse,
  SessionInfo,
  VoteDirection,
  Video,
} from "./types";

/** Photos */
export const getPhotos = (page = 0, size = 24) =>
  http.get<PageResponse<Photo>>(`/api/photos?page=${page}&size=${size}`);

/** The photos flagged for the home page, in front-page order. */
export const getFeaturedPhotos = (page = 0, size = 60) =>
  http.get<PageResponse<Photo>>(
    `/api/photos?featured=true&page=${page}&size=${size}`,
  );

export const getPhoto = (id: number) => http.get<Photo>(`/api/photos/${id}`);

/** Videos */
export const getVideos = (page = 0, size = 24) =>
  http.get<PageResponse<Video>>(`/api/videos?page=${page}&size=${size}`);

/** The videos flagged for the home page, in front-page order. */
export const getFeaturedVideos = (page = 0, size = 60) =>
  http.get<PageResponse<Video>>(
    `/api/videos?featured=true&page=${page}&size=${size}`,
  );
export const getVideo = (id: number) => http.get<Video>(`/api/videos/${id}`);

/** External resources */
export const getResources = (page = 0, size = 24) =>
  http.get<PageResponse<ExternalResource>>(`/api/resources?page=${page}&size=${size}`);

/** Blog posts */
export const getPosts = (page = 0, size = 10) =>
  http.get<PageResponse<BlogPostSummary>>(`/api/blog/posts?page=${page}&size=${size}`);
export const getPost = (slug: string) =>
  http.get<BlogPost>(`/api/blog/posts/${encodeURIComponent(slug)}`);

/** Blog feed — faceted search restricted to non-photo content. */
export const getBlogFeed = (params: SearchParams) =>
  http.get<SearchResponse>(`/api/blog/feed${buildSearchQuery(params)}`);

/** Site-wide search across every content type. */
export const search = (params: SearchParams) =>
  http.get<SearchResponse>(`/api/search${buildSearchQuery(params)}`);

/** The collections the library is organised into (drives nav + section pages). */
export const getCollections = () => http.get<Collection[]>("/api/collections");

/** Contact form submission (public). */
export const submitContact = (body: ContactRequest) =>
  http.post<unknown>("/api/contact", body);

/** Unified document detail by content id (e.g. "PHOTO-7", "BLOG-POST-3"). */
export const getDocument = (contentId: string) =>
  http.get<DocumentDto>(`/api/documents/${encodeURIComponent(contentId)}`);

/** Comments */
export const getComments = (contentType: ContentType, sourceId: number) =>
  http.get<CommentThreadResponse>(
    `/api/comments?type=${contentType}&sourceId=${sourceId}`,
  );

export const postComment = (body: CommentRequest) =>
  http.post<CommentDto>("/api/comments", body);

export const voteComment = (id: number, direction: VoteDirection) =>
  http.post<CommentVoteDto>(`/api/comments/${id}/vote`, { direction });

/**
 * Member sessions. Signing in sets an HttpOnly cookie, which the browser then
 * sends with every same-origin request (fetch's default) — API calls and image
 * loads alike — so nothing here stores a credential.
 */
export const getSession = () => http.get<SessionInfo>("/api/session");

export const signIn = (email: string, password: string) =>
  http.post<SessionInfo>("/api/session/login", { email, password });

export const signOut = () => http.post<void>("/api/session/logout", {});

export const changePassword = (currentPassword: string, newPassword: string) =>
  http.post<void>("/api/session/password", { currentPassword, newPassword });
